<script setup>

    import { ref, computed, shallowRef } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useEngagementStore } from '@/Stores'

    import { vElementHover } from '@vueuse/components'
    import { CARE_RECIPIENT_CONTACT_TYPE, fullName, util } from '@/Helpers'

    import LatteRatingBar from '@/Components/Feedback/LatteRatingBar.vue'
    import Tag from '@/Components/Tag.vue'
    import InputLabel from '@/Components/Forms/InputLabel.vue'
    import LwxSwitch from '@/Components/Forms/LwxSwitch.vue'
    import { ChevronRightIcon, PencilSquareIcon, ExclamationTriangleIcon } from '@heroicons/vue/20/solid'

    const engagementStore = useEngagementStore()
    const { opportunitiesData, scheduledEngagementData, loading } = storeToRefs(engagementStore)

    const props = defineProps({
        scope: {
            type: Array,
            default: () => [ 'all' ] // Possible values: 'prospects', 'clients', 'all'
        },
        columns: {
            type: Array,
            default: () => [ 'recipient', 'location' ]
        }
    })

    const filter = ref({
        client: true,
        caregiver: true,
        coi: true,
    })

    const isExpanderHovered = shallowRef(false)

    function onHoverExpander(state) {
        isExpanderHovered.value = state
    }

    const expandedRows = ref({})
    const selectedOpportunity = ref(null)

    const rowClass = (data) => {
        return [{
            'group': !isExpanderHovered.value
        }, '[&>td]:!border-none user-select-none'];
    }

    function onRowSelect(event) {
        engagementStore.selectOpportunity(event.data.id, event.data.type)
        selectedOpportunity.value = null
        util.clearFocus()
    }

    function onChildRowSelect(event) {
        engagementStore.selectOpportunity(event.data.id, 'contact')
        selectedOpportunity.value = null
        util.clearFocus()
    }

    const transformedOpportunitiesData = computed(() => {
        return opportunitiesData.value.map(recipient => {

            // Determine the type based on the boolean flags
            let type = 'client'
            if (recipient.is_caregiver) {
                type = 'caregiver'
            } else if (recipient.is_coi && !recipient.is_client && !recipient.is_caregiver) {
                type = 'coi'
            }

            // Build the location string from address
            let location = ''
            if (recipient.address) {
                const parts = [ recipient.address.city, recipient.address.state ].filter(Boolean);
                location = parts.join(', ')
            }

            // Filter and format location for each contact
            const contactsTransformed = recipient.contacts.filter(contact => contact.type != CARE_RECIPIENT_CONTACT_TYPE && !existingRequestIdTypeArr.value.includes('contact-' + contact.id)).map(contact => ({
                ...contact,
                location: [ contact.city, contact.state ].filter(Boolean).join(', '),
                address_validated: Boolean(contact.address && contact.city && contact.state),
            }))

            // Transform the data to match the expected format
            const transformedRecipient = {
                uniqueId: type + '-' + recipient.id,
                id: recipient.id,
                first_name: recipient.first_name,
                last_name: recipient.last_name,
                type: type,
                is_coi: recipient.is_coi,
                is_caregiver: recipient.is_caregiver,
                is_client: recipient.is_client,
                location: location,
                address: recipient.address || {},
                address_validated: Boolean(recipient.address?.address && recipient.address?.city && recipient.address?.state),
                latest_rating_per_job: recipient.latest_rating_per_job || [],
                contacts: contactsTransformed
            }

            return transformedRecipient;
        }).sort((a, b) => {
            // Sort by last name, then first name
            const aSortKey = [a.last_name, a.first_name].filter(Boolean).join(', ').toLowerCase();
            const bSortKey = [b.last_name, b.first_name].filter(Boolean).join(', ').toLowerCase();
            if (aSortKey < bSortKey) return -1;
            if (aSortKey > bSortKey) return 1;
            return 0;
        })
    })

    const existingRequestIdTypeArr = computed(() => {
        return scheduledEngagementData.value.map(request => {
            return request.recipient.type + '-' + request.recipient.id
        })
    })

    const filteredOpportunities = computed(() => {
        return transformedOpportunitiesData.value.filter(opportunity => {
            if (existingRequestIdTypeArr.value.includes(opportunity.type + '-' + opportunity.id)) {
                return false
            }

            if (opportunity.is_coi && filter.value.coi) {
                return true
            }

            if (opportunity.is_client && filter.value.client) {
                return true
            }

            if (opportunity.is_caregiver && filter.value.caregiver) {
                return true
            }

            return false
        })
    })

    const handleToggleRow = (uniqueId) => {
        if (expandedRows.value[uniqueId]) {
            delete expandedRows.value[uniqueId] // Collapse
        } else {
            expandedRows.value[uniqueId] = true // Expand
        }
    }

    const isRowExpanded = (uniqueId) => {
        return expandedRows.value[uniqueId] === true
    }

</script>

<template>

    <DataTable
        v-model:expandedRows="expandedRows"
        v-model:selection="selectedOpportunity"
        :value="filteredOpportunities"
        :pt="{
            root: 'text-xs',
            header: 'bg-lifeworx-blue-600/5',
            tablecontainer: '!overflow-visible',
            thead: 'sticky z-10 top-28 bg-white/75 backdrop-blur backdrop-filter',
            rowexpansioncell: 'p-0',
        }"
        :loading="loading"
        selectionMode="single"
        @rowSelect="onRowSelect"
        expander
        dataKey="uniqueId"
        :rowClass="rowClass"
        :expandableRow="row => row.contacts && row.contacts.length > 0"
        :stripedRows="true"
    >
        <template #header>
            <div class="flex justify-end pt-3 pb-2 gap-8 mx-4">
                <span class="font-semibold text-gray-400 text-sm">
                    Show:
                </span>
                <InputLabel
                    for="client-only"
                    value="Clients"
                    class="text-sm w-fit flex gap-2 cursor-pointer text-lifeworx-green-600"
                    palette="blue"
                    position="right"
                >
                    <LwxSwitch
                        id="client-only"
                        v-model="filter.client"
                        palette="green"
                    />
                </InputLabel>
                <InputLabel
                    for="caregiver-only"
                    value="Caregivers"
                    class="text-sm w-fit flex gap-2 cursor-pointer text-lifeworx-blue-600"
                    palette="blue"
                    position="right"
                >
                    <LwxSwitch
                        id="caregiver-only"
                        v-model="filter.caregiver"
                        palette="blue"
                    />
                </InputLabel>
                <InputLabel
                    for="coi-only"
                    value="COIs"
                    class="text-sm w-fit flex gap-2 cursor-pointer text-lifeworx-purple-500"
                    palette="purple"
                    position="right"
                >
                    <LwxSwitch
                        id="coi-only"
                        v-model="filter.coi"
                        palette="purple"
                    />
                </InputLabel>
            </div>
        </template>

        <template #empty>
            <div class="py-28 text-center font-bold">No opportunities found.</div>
        </template>

        <Column
            class="w-20"
            expander
        >
            <template #body="{ data }">
                <div class="p-2 w-full h-full flex justify-end items-center">
                    <div
                        v-if="data.type === 'caregiver' || data.type === 'coi'"
                        class="size-8"
                    />
                    <div
                        v-if="data.type === 'client' && data.contacts && data.contacts.length > 0"
                        @click.stop="handleToggleRow(data.uniqueId)"
                        v-element-hover="onHoverExpander"
                        :class="[
                            'size-8 rounded-full cursor-pointer flex items-center justify-center transition-transform duration-75 select-none',
                            {
                                'bg-lifeworx-yellow-600/20 hover:bg-yellow-600/40 text-lifeworx-yellow-600 hover:text-white hover:rotate-90': !isRowExpanded(data.uniqueId),
                                'bg-lifeworx-yellow-600/20 hover:bg-yellow-600/40 text-lifeworx-yellow-600 hover:text-white rotate-90 hover:-rotate-90': isRowExpanded(data.uniqueId)
                            }
                        ]"
                    >
                        <ChevronRightIcon
                            :class="[
                                'size-6 ',
                                {
                                }
                            ]"
                        />
                    </div>
                </div>
            </template>
        </Column>

        <Column
            header="RECIPIENT"
            field="recipient"
            class="lg:w-[23rem]"
        >
            <template #body="{ data }">
                <div class="flex gap-2">
                    <span class="text-base font-semibold text-lifeworx-blue-800">
                        {{ fullName(data.first_name, data.last_name) }}
                    </span>

                    <Tag
                        v-if="data.type == 'client'"
                        class="text-xs"
                        color="green"
                    >
                        {{ data.type }}
                    </Tag>
                    <Tag
                        v-if="data.type === 'caregiver'"
                        class="text-xs"
                        color="blue"
                    >
                        {{ data.type }}
                    </Tag>
                    <Tag
                        v-if="data.is_coi === true"
                        class="text-xs"
                        color="purple"
                    >
                        COI
                    </Tag>

                </div>

                <template v-if="data.type === 'client' || data.type === 'caregiver'">
                    <div class="w-60 -ml-0.5 mt-1">
                        <LatteRatingBar
                            :subject="data"
                            :subjectType="data.type"
                        />
                    </div>
                </template>

            </template>
        </Column>

        <Column
            header="LOCATION"
            field="location"
            class="hidden md:table-cell"
        >
            <template #body="{ data }">
                <div class="flex">
                    <ExclamationTriangleIcon
                        v-if="data.address_validity === 'N' || !data.address_validated"
                        class="text-lifeworx-red-600 inline-block size-6 -ml-1.5 mr-1.5"
                    />
                    <span
                        :class="[
                            'text-sm font-semibold text-nowrap',
                            {
                                'italic text-lifeworx-red-600': !data.location,
                                'text-stone-400': data.location
                            }
                        ]"
                    >
                        {{ data.location ? data.location : 'Missing' }}
                    </span>
                </div>
            </template>
        </Column>

        <Column
            header="ACTION"
            class="hidden md:table-cell w-20 lg:w-60"
        >
            <template #body="{ data }">
                <div
                    :class="[
                        'py-1 px-3 rounded-full flex items-center justify-center group-hover:text-white',
                        {
                            'text-lifeworx-red-600 group-hover:bg-lifeworx-red-600': data.address_validated === false,
                            'text-lifeworx-green-500 group-hover:bg-lifeworx-green-500': data.address_validated === true
                        }
                    ]"
                >
                    <span
                        v-if="data.address_validated === false"
                        class="flex items-center text-sm font-semibold text-nowrap"
                    >
                        <span class="hidden lg:inline">Fix Address</span>
                        <ChevronRightIcon class="size-6 lg:ml-1 lg:-mr-2 inline-block" />
                    </span>
                    <span v-else
                        class="flex items-center text-sm font-semibold text-nowrap"
                    >
                        <span class="hidden lg:inline">Schedule a Gift</span>
                        <ChevronRightIcon class="size-6 lg:ml-1 lg:-mr-1 inline-block" />
                    </span>
                </div>
            </template>
        </Column>


        <template #expansion="slotProps">

            <div class="mb-4">
                <DataTable
                    v-if="slotProps.data.contacts.length > 0"
                    :value="slotProps.data.contacts"
                    :pt="{
                        root: 'text-xs',
                        thead: 'hidden',
                        tablecontainer: '!overflow-visible',
                        bodycell: '!border-none'
                    }"
                    selectionMode="single"
                    @rowSelect="onChildRowSelect"
                    dataKey="id"
                    :rowClass="rowClass"
                >
                    <Column
                        class="w-20"
                    >
                        <template #body="{ data }">
                            <div class="p-2 w-full h-full flex justify-center items-center">
                                <div class="rounded-full size-3 bg-lifeworx-yellow-600/20" />
                            </div>
                        </template>
                    </Column>

                    <Column
                        field="recipient"
                        class="lg:w-[23rem]"
                    >
                        <template #body="{ data }">
                            <div class="flex gap-2">
                                <Tag
                                    class="text-xs"
                                    color="yellow"
                                >
                                    Contact
                                </Tag>
                                <span class="text-base font-semibold text-lifeworx-blue-800">
                                    {{ data.first_name }} {{ data.last_name }}
                                </span>
                                <Tag
                                    v-if="data.type !== null"
                                    class="text-xs"
                                    color="green"
                                >
                                    {{ data.type }}
                                </Tag>
                            </div>
                        </template>
                    </Column>

                    <Column
                        field="location"
                        class="hidden md:table-cell"
                    >
                        <template #body="{ data }">
                            <div class="flex">
                                <ExclamationTriangleIcon
                                    v-if="data.address_validity === 'N' || !data.address_validated"
                                    class="text-lifeworx-red-600 inline-block size-6 -ml-1.5 mr-1.5"
                                />
                                <span
                                    :class="[
                                        'text-sm font-semibold text-nowrap',
                                        {
                                            'italic text-lifeworx-red-600': !data.location,
                                            'text-stone-400': data.location
                                        }
                                    ]"
                                >
                                    {{ data.location ? data.location : 'Missing' }}
                                </span>
                            </div>
                        </template>
                    </Column>

                    <Column
                        class="hidden md:table-cell w-20 lg:w-60"
                    >
                        <template #body="{ data }">
                            <div
                                :class="[
                                    'py-1 px-3 rounded-full flex items-center justify-center group-hover:text-white',
                                    {
                                        'text-lifeworx-red-600 group-hover:bg-lifeworx-red-600': data.address_validated === false,
                                        'text-lifeworx-green-500 group-hover:bg-lifeworx-green-500': data.address_validated === true
                                    }
                                ]"
                            >
                                <span
                                    v-if="data.address_validated === false"
                                    class="flex items-center text-sm font-semibold text-nowrap"
                                >
                                    <span class="hidden lg:inline">Fix Address</span>
                                    <ChevronRightIcon class="size-6 lg:ml-1 lg:-mr-2 inline-block" />
                                </span>
                                <span v-else
                                    class="flex items-center text-sm font-semibold text-nowrap"
                                >
                                    <span class="hidden lg:inline">Schedule a Gift</span>
                                    <ChevronRightIcon class="size-6 lg:ml-1 lg:-mr-1 inline-block" />
                                </span>
                            </div>
                        </template>
                    </Column>

                </DataTable>
            </div>

        </template>

    </DataTable>

</template>

<style>

    .hide-expander td .p-datatable-row-toggle-button {
        display: none !important;
    }

    .p-datatable-row-expansion td div div table tbody tr td {
        border-bottom-width: 0 !important;
    }

    .p-datatable-row-expansion {
        background: white;
    }

</style>
